<script lang="ts">
  import { getCookie, getUserId } from '~/auth/utils';
  import { dev } from '$app/env';
  import { onMount, type Snippet } from 'svelte';
  import '~/theme/global.scss';
  import '~/theme/splash.scss';
  import { SvelteQueryDevtools } from '@tanstack/svelte-query-devtools';
  import * as Sentry from '@sentry/svelte';
  import {
    PersistQueryClientProvider,
  } from '@tanstack/svelte-query-persist-client';
  import SupabaseProvider from '~/lib/SupabaseProvider.svelte';
  import { asyncStoragePersister } from '~/store/asyncStorage';
  import { hydrated } from '~/tutorial/state.svelte';
  import { initUserMutation } from '~/auth/mutations.svelte';
  import type { LayoutData } from './$types';
  import { initNavTracking, initIdleNavTracking, useUnload } from '~/lib/nav.svelte';
  import { verifyEmailModal } from '~/auth/Verification.svelte';
  import Drawer from '~/components/Drawer.svelte';
  import {
    dictionaryLoaded,
    getDictionary,
    getDictionaryWorker,
  } from '~/algorithms/dictionary.svelte';
  import PageCrossfade from '~/utils/PageCrossfade.svelte';
  import AudioPlayer from '~/components/AudioPlayer.svelte';
  import AppToaster from '~/components/AppToaster.svelte';
  import PremiumModal from '~/modals/PremiumModal.svelte';
  import Definition from '~/modals/Definition.svelte';
  import AuthModals from '~/auth/AuthModals.svelte';
  import { invalidate, goto } from '$app/navigation';
  import { page } from '$app/state';
  import { toast } from 'svelte-sonner';
  import PremiumListener from '~/auth/PremiumListener.svelte';
  import { sessionId, pendingVerification } from '~/auth/utils.svelte';
  type Props = {
    data: LayoutData;
    children: Snippet;
  };
  const { data, children }: Props = $props();

  initNavTracking();
  initIdleNavTracking();

  onMount(async () => {
    let method: string;
    try {
      const workerLoad = getDictionaryWorker().load();
      await workerLoad;
      dictionaryLoaded.current = workerLoad;
      method = 'worker';
    } catch {
      dictionaryLoaded.current = (await getDictionary()).load();
      method = 'local';
    }
    const [t, _] = await Promise.all([
      dictionaryLoaded.current,
      import('../pwa'),
    ]);
    if (!dev) {
      await data.supabase.from('click_analytics').insert({
        userId: getUserId(),
        buttonId: `loaded dictionary in ${t}s from ${method}`,
      });
    }
  });

  // svelte-ignore state_referenced_locally
  const initUser = initUserMutation(data.queryClient);
  // Stable reference so PersistQueryClientProvider's $derived options doesn't
  // recompute on every render and re-trigger the isRestoring restore cycle.
  const persistOptions = { persister: asyncStoragePersister };

  onMount(() => {
    hydrated.current = true;
    const prevVersion = localStorage.getItem('version');
    const version = getCookie('version');
    const userId = getCookie('userId');
    if (version != prevVersion) {
      localStorage.setItem('version', version!);
      // data.queryClient.clear();
      // TODO may need to remove this
      // invalidateAll();
    }
    Sentry.setTag('version', version);
    Sentry.setTag('session', sessionId.current);
    Sentry.setUser({
      id: userId,
      name: localStorage.getItem('displayName'),
    });

    const authListener = data.supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (newSession?.expires_at !== data.session?.expires_at) {
          invalidate('supabase:auth');
        }
        // Use newSession.user directly instead of calling getUser() — calling any
        // Supabase auth method inside onAuthStateChange deadlocks because signOut()
        // holds the auth lock while awaiting subscriber callbacks.
        const user = newSession?.user;
        if (user?.id) {
          Sentry.setUser({
            id: user.id,
            name: localStorage.getItem('displayName'),
            email: user.email,
          });
          // Dispatch async work after the callback exits to avoid holding up signOut().
          setTimeout(async () => {
            if (!initUser.isSuccess) {
              await initUser.mutateAsync();
            }
          }, 0);
      }
      // manage auth modals
      if (data.user) {
        pendingVerification.current = '';
      }
      },
    );

    useUnload();
    // #error=unauthorized_client&error_code=403&error_description=Email+link+is+invalid+or+has+expired
    if (window.location.href.includes('error_code=403')) {
      verifyEmailModal.push('error');
    }

    if (page.url.searchParams.get('signin') === 't') {
      toast.success('logged in');
      const cleaned = new URL(page.url.href);
      cleaned.searchParams.delete('signin');
      goto(cleaned.pathname + cleaned.search + cleaned.hash, { shallow: true, replace: true, state: page.state });
    }

    return authListener.data.subscription.unsubscribe;
  });
</script>

<svelte:head>
  <script>
    if (document) {
      let mode = globalThis.localStorage?.theme || 'light';
      if (
        mode === 'dark' ||
        (mode !== 'light' &&
          window.matchMedia('(prefers-color-scheme: dark)').matches)
      ) {
        document.documentElement.classList.add('dark-mode');
        document.documentElement.classList.add('theme-dark');
        localStorage.theme = 'dark';
      } else {
        document.documentElement.classList.add('theme-light');
        document.documentElement.classList.remove('dark-mode');
        document.documentElement.classList.remove('theme-dark');
        localStorage.theme = 'light';
      }
      // render splash animation after theme is set
      document.documentElement.classList.add('loaded');
    }
  </script>
  <!-- Google tag (gtag.js) -->
  <script
    async
    src="https://www.googletagmanager.com/gtag/js?id=AW-16677965421"
  ></script>

  <script>
    window.dataLayer = window.dataLayer || []; function gtag() { dataLayer.push(arguments); } gtag('js', new Date()); gtag('config', 'AW-16677965421');
  </script>
</svelte:head>

<SupabaseProvider client={data.supabase}>
  <PersistQueryClientProvider
    client={data.queryClient}
    persistOptions={persistOptions}
  >
    <main>
      <PageCrossfade>
        {@render children()}
      </PageCrossfade>
      <Drawer />
      <PremiumModal />
      <PremiumListener />
      <Definition />
      <AuthModals />
    </main>
    {#if dev && page.route.id !== '/splash'}
      <div class="spacer"></div>
      <!--<DebugControls />-->
      <SvelteQueryDevtools initialIsOpen={false} />
    {/if}
  </PersistQueryClientProvider>
</SupabaseProvider>
<AudioPlayer />
<AppToaster />

<style lang="scss">
  @use '~/theme/themify' as *;

  main {
    display: grid;
    grid-template-rows: 100%;
    width: 100%;
    height: 100%;
    text-align: center;
    overflow: hidden;
    user-select: none;
    align-items: center;
    padding: 0;
    margin: 0 auto;
    overscroll-behavior: contain;
    @include themify {
      color: themed('text');
      background-color: themed('background');
    }
  }
</style>
